import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Stack,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    TrendingUp,
    ShoppingCart,
    Inventory,
    AttachMoney,
    People,
    Add,
    PointOfSale,
    Refresh,
    CheckCircle,
    Schedule,
    LocalShipping,
} from "@mui/icons-material";
import { orderService } from "../services/orderService";
import { formatCurrencyBDT } from "../utils/currency";
import { Product, Order, Stock, DashboardSummary, TimePreset } from "../types";
import { useNavigate } from "react-router-dom";
import { TrendingDown, HourglassEmpty } from "@mui/icons-material";
import { usePermissions } from "../hooks/usePermissions";

interface DashboardData {
    analytics: DashboardSummary | null;
    lowStockProducts: Product[];
    recentOrders: Order[];
    stockAlerts: Stock[];
}

export const Dashboard: React.FC = () => {
    const { canAccessArea } = usePermissions();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [dashboardData, setDashboardData] = useState<DashboardData>({
        analytics: null,
        lowStockProducts: [],
        recentOrders: [],
        stockAlerts: [],
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [timeRange, setTimeRange] = useState<TimePreset>("this_month");

    // Custom date range state
    const [customDialogOpen, setCustomDialogOpen] = useState(false);
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError("");

            const analyticsParams: any = {
                preset: timeRange,
                include_comparison:
                    timeRange !== "all_time" && timeRange !== "custom",
            };

            if (timeRange === "custom") {
                if (startDate) analyticsParams.start_date = startDate;
                if (endDate) analyticsParams.end_date = endDate;
            }

            // Fetch all dashboard data in parallel
            const [analytics, recentOrdersResponse] = await Promise.all([
                orderService
                    .getDashboardAnalytics(analyticsParams)
                    .catch(() => null),
                orderService
                    .getOrders({ limit: 10 })
                    .catch(() => ({ items: [], total: 0, skip: 0, limit: 10 })),
            ]);

            // For now, we'll use empty arrays for stock data until we implement the stock service method
            const lowStockProducts: Product[] = [];
            const stockAlerts: Stock[] = [];

            setDashboardData({
                analytics,
                lowStockProducts,
                recentOrders: recentOrdersResponse.items || [],
                stockAlerts,
            });
        } catch (err: any) {
            setError("Failed to load dashboard data");
            console.error("Dashboard data fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (timeRange !== "custom") {
            fetchDashboardData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeRange]);

    const handleCustomDateClick = () => {
        setCustomDialogOpen(true);
    };

    const handleApplyCustomDate = () => {
        setTimeRange("custom");
        setCustomDialogOpen(false);
        fetchDashboardData();
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 400,
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    const formatCurrency = (amount: number, ignoreDecimals?: boolean) =>
        formatCurrencyBDT(amount, { ignoreDecimals });
    const { analytics, recentOrders, stockAlerts } = dashboardData;

    const getTrendColor = (value: number | null | undefined) => {
        if (value === null || value === undefined) return "text.secondary";
        return value >= 0 ? "success.main" : "error.main";
    };

    const getTrendIcon = (value: number | null | undefined) => {
        if (value === null || value === undefined) return null;
        return value >= 0 ? (
            <TrendingUp sx={{ fontSize: 16 }} />
        ) : (
            <TrendingDown sx={{ fontSize: 16 }} />
        );
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case "DELIVERED":
                return <CheckCircle sx={{ color: "success.main" }} />;
            case "SHIPPED":
                return <LocalShipping sx={{ color: "info.main" }} />;
            case "PENDING":
                return <Schedule sx={{ color: "warning.main" }} />;
            default:
                return <ShoppingCart sx={{ color: "text.secondary" }} />;
        }
    };

    return (
        <Box>
            {/* Header with Quick Actions */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 4,
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="h4"
                        component="h1"
                        gutterBottom
                        sx={{ fontSize: { xs: "1.35rem", sm: "2.125rem" } }}
                    >
                        Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {analytics
                            ? `${analytics.period_label} (${analytics.period_start} to ${analytics.period_end})`
                            : "Welcome to Catelyn Pet Shop Admin Dashboard"}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        width: { xs: "100%", sm: "auto" },
                    }}
                >
                    {/* Time Range Chips */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 0.5,
                            flexWrap: "wrap",
                            width: { xs: "100%", sm: "auto" },
                        }}
                    >
                        <Chip
                            label="Today"
                            size="small"
                            onClick={() => setTimeRange("today")}
                            color={
                                timeRange === "today" ? "primary" : "default"
                            }
                            variant={
                                timeRange === "today" ? "filled" : "outlined"
                            }
                        />
                        <Chip
                            label="This Week"
                            size="small"
                            onClick={() => setTimeRange("this_week")}
                            color={
                                timeRange === "this_week"
                                    ? "primary"
                                    : "default"
                            }
                            variant={
                                timeRange === "this_week"
                                    ? "filled"
                                    : "outlined"
                            }
                        />
                        <Chip
                            label="This Month"
                            size="small"
                            onClick={() => setTimeRange("this_month")}
                            color={
                                timeRange === "this_month"
                                    ? "primary"
                                    : "default"
                            }
                            variant={
                                timeRange === "this_month"
                                    ? "filled"
                                    : "outlined"
                            }
                        />
                        <Chip
                            label="Last Month"
                            size="small"
                            onClick={() => setTimeRange("last_month")}
                            color={
                                timeRange === "last_month"
                                    ? "primary"
                                    : "default"
                            }
                            variant={
                                timeRange === "last_month"
                                    ? "filled"
                                    : "outlined"
                            }
                        />
                        <Chip
                            label="All Time"
                            size="small"
                            onClick={() => setTimeRange("all_time")}
                            color={
                                timeRange === "all_time" ? "primary" : "default"
                            }
                            variant={
                                timeRange === "all_time" ? "filled" : "outlined"
                            }
                        />
                        <Chip
                            label="Custom"
                            size="small"
                            onClick={handleCustomDateClick}
                            color={
                                timeRange === "custom" ? "primary" : "default"
                            }
                            variant={
                                timeRange === "custom" ? "filled" : "outlined"
                            }
                        />
                    </Box>
                    {/* Action Buttons */}
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<Refresh />}
                            onClick={fetchDashboardData}
                            disabled={loading}
                            size="small"
                        >
                            Refresh
                        </Button>
                        {canAccessArea("operations") && (
                            <Button
                                variant="contained"
                                startIcon={<PointOfSale />}
                                onClick={() => navigate("/pos")}
                                size="small"
                            >
                                {isMobile ? "POS" : "Open POS"}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Stock Alerts */}
            {stockAlerts.length > 0 && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Low Stock Alert
                    </Typography>
                    <Typography variant="body2">
                        {stockAlerts.length} products are running low on stock.
                        <Button
                            size="small"
                            onClick={() => navigate("/inventory/stock")}
                            sx={{ ml: 1 }}
                        >
                            View Stock
                        </Button>
                    </Typography>
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Revenue Stats - Only from DELIVERED orders */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Box>
                                    <Typography
                                        color="textSecondary"
                                        gutterBottom
                                        variant="overline"
                                    >
                                        Revenue (Delivered)
                                    </Typography>
                                    <Typography variant="h4" component="h2">
                                        {formatCurrency(
                                            analytics?.total_revenue || 0,
                                            true,
                                        )}
                                    </Typography>
                                    {analytics?.revenue_change_percent !==
                                        null &&
                                        analytics?.revenue_change_percent !==
                                            undefined && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: getTrendColor(
                                                        analytics.revenue_change_percent,
                                                    ),
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                            >
                                                {getTrendIcon(
                                                    analytics.revenue_change_percent,
                                                )}
                                                <span style={{ marginLeft: 4 }}>
                                                    {analytics.revenue_change_percent >
                                                    0
                                                        ? "+"
                                                        : ""}
                                                    {analytics.revenue_change_percent.toFixed(
                                                        1,
                                                    )}
                                                    % vs prev
                                                </span>
                                            </Typography>
                                        )}
                                </Box>
                                <Box
                                    sx={{
                                        backgroundColor: "success.main",
                                        borderRadius: "50%",
                                        p: 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <AttachMoney sx={{ color: "white" }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Total Orders */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Box>
                                    <Typography
                                        color="textSecondary"
                                        gutterBottom
                                        variant="overline"
                                    >
                                        Total Orders
                                    </Typography>
                                    <Typography variant="h4" component="h2">
                                        {analytics?.total_orders || 0}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {analytics?.delivered_orders || 0}{" "}
                                        delivered
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        backgroundColor: "primary.main",
                                        borderRadius: "50%",
                                        p: 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <ShoppingCart sx={{ color: "white" }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Fulfillment Rate */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Box>
                                    <Typography
                                        color="textSecondary"
                                        gutterBottom
                                        variant="overline"
                                    >
                                        Fulfillment Rate
                                    </Typography>
                                    <Typography variant="h4" component="h2">
                                        {analytics?.fulfillment_rate?.toFixed(
                                            1,
                                        ) || 0}
                                        %
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {analytics?.cancelled_orders || 0}{" "}
                                        cancelled (
                                        {analytics?.cancellation_rate?.toFixed(
                                            1,
                                        ) || 0}
                                        %)
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        backgroundColor: "info.main",
                                        borderRadius: "50%",
                                        p: 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <CheckCircle sx={{ color: "white" }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Potential Revenue */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Box>
                                    <Typography
                                        color="textSecondary"
                                        gutterBottom
                                        variant="overline"
                                    >
                                        Pending Revenue
                                    </Typography>
                                    <Typography variant="h4" component="h2">
                                        {formatCurrency(
                                            analytics?.potential_revenue || 0,
                                            true,
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {analytics?.pending_orders || 0} pending
                                        orders
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        backgroundColor: "warning.main",
                                        borderRadius: "50%",
                                        p: 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <HourglassEmpty sx={{ color: "white" }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Order Status Overview */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Order Status Overview
                            </Typography>
                            {analytics?.orders_by_status ? (
                                <Box sx={{ mt: 2 }}>
                                    {Object.entries(
                                        analytics.orders_by_status,
                                    ).map(([status, count]) => {
                                        const percentage =
                                            analytics.total_orders > 0
                                                ? (
                                                      (count /
                                                          analytics.total_orders) *
                                                      100
                                                  ).toFixed(0)
                                                : 0;
                                        return (
                                            <Box key={status} sx={{ mb: 2 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "space-between",
                                                        alignItems: "center",
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        {getStatusIcon(status)}
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {status.charAt(0) +
                                                                status
                                                                    .slice(1)
                                                                    .toLowerCase()}
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                    >
                                                        {count} ({percentage}%)
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={
                                                        analytics.total_orders >
                                                        0
                                                            ? (count /
                                                                  analytics.total_orders) *
                                                              100
                                                            : 0
                                                    }
                                                    sx={{
                                                        height: 6,
                                                        borderRadius: 3,
                                                    }}
                                                    color={
                                                        status === "DELIVERED"
                                                            ? "success"
                                                            : status ===
                                                                "SHIPPED"
                                                              ? "info"
                                                              : status ===
                                                                  "CANCELLED"
                                                                ? "error"
                                                                : status ===
                                                                    "CONFIRMED"
                                                                  ? "primary"
                                                                  : "warning"
                                                    }
                                                />
                                            </Box>
                                        );
                                    })}
                                </Box>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    No order data available
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Recent Orders */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 2,
                                }}
                            >
                                <Typography variant="h6">
                                    Recent Orders
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={() => navigate("/orders")}
                                >
                                    View All
                                </Button>
                            </Box>
                            {recentOrders.length > 0 ? (
                                <List dense>
                                    {recentOrders.slice(0, 5).map((order) => (
                                        <ListItem
                                            key={order.order_id}
                                            sx={{ px: 0 }}
                                        >
                                            <ListItemIcon>
                                                {getStatusIcon(order.status)}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={`Order #${order.order_number}`}
                                                secondary={`${formatCurrency(
                                                    order.total_amount,
                                                )} - ${
                                                    order.customer
                                                        ?.first_name ||
                                                    "Walk-in"
                                                } ${
                                                    order.customer?.last_name ||
                                                    "Customer"
                                                }`}
                                            />
                                            <Chip
                                                label={order.status}
                                                size="small"
                                                color={
                                                    order.status === "DELIVERED"
                                                        ? "success"
                                                        : order.status ===
                                                            "SHIPPED"
                                                          ? "info"
                                                          : order.status ===
                                                              "PENDING"
                                                            ? "warning"
                                                            : "default"
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    No recent orders
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Top Products */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 2,
                                }}
                            >
                                <Box>
                                    <Typography variant="h6">
                                        Top Selling Products
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Based on delivered orders only
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    onClick={() => navigate("/products")}
                                >
                                    View All
                                </Button>
                            </Box>
                            {analytics?.top_products &&
                            analytics.top_products.length > 0 ? (
                                <Box sx={{ mt: 2 }}>
                                    {analytics.top_products
                                        .slice(0, 5)
                                        .map((product, index: number) => (
                                            <Box
                                                key={product.product_id}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    mb: 2,
                                                }}
                                            >
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="medium"
                                                    >
                                                        {index + 1}.{" "}
                                                        {product.name}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {product.quantity_sold}{" "}
                                                        units sold
                                                    </Typography>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={Math.min(
                                                            (product.quantity_sold /
                                                                (analytics
                                                                    .top_products?.[0]
                                                                    ?.quantity_sold ||
                                                                    1)) *
                                                                100,
                                                            100,
                                                        )}
                                                        sx={{
                                                            mt: 0.5,
                                                            height: 4,
                                                            borderRadius: 2,
                                                        }}
                                                        color="success"
                                                    />
                                                </Box>
                                                <Box
                                                    sx={{
                                                        ml: 2,
                                                        textAlign: "right",
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                    >
                                                        {formatCurrency(
                                                            product.revenue,
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                </Box>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    No sales data available for this period
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Quick Actions */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Quick Actions
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    startIcon={<Add />}
                                    onClick={() => navigate("/products")}
                                    fullWidth
                                >
                                    Add New Product
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<People />}
                                    onClick={() => navigate("/customers")}
                                    fullWidth
                                >
                                    Add New Customer
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<ShoppingCart />}
                                    onClick={() => navigate("/orders")}
                                    fullWidth
                                >
                                    Create Order
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<Inventory />}
                                    onClick={() => navigate("/inventory/stock")}
                                    fullWidth
                                >
                                    Manage Stock
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Custom Date Range Dialog */}
            <Dialog
                open={customDialogOpen}
                onClose={() => setCustomDialogOpen(false)}
            >
                <DialogTitle>Select Custom Date Range</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1, minWidth: 300 }}>
                        <TextField
                            label="Start Date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                            fullWidth
                        />
                        <TextField
                            label="End Date"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCustomDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleApplyCustomDate}
                        variant="contained"
                        disabled={!startDate || !endDate}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
